import React from 'react';
import { AutomateAccountingIcon, Badge, Box, Text } from '@razorpay/blade/components';
import type { OtSalaryComponent } from './hooks/useOtSalaryComponents';
import { OTRateType, OTRateUnit } from 'api/gql-utils/overtime/types';
import { DottedGridPanel } from './styles';
import { FORMULA_UNIT_DISPLAY } from 'components/Settings/AttendanceAndLeavesSettings/constants';

export const FormulaPreview = ({
  rateType,
  flatAmount,
  multiplier,
  unit,
  dailyAllowanceAmount,
  showDailyAllowance,
  previewComponents,
  baseDays,
  baseHours,
}: {
  rateType?: OTRateType;
  flatAmount?: number;
  multiplier?: string;
  unit?: OTRateUnit;
  dailyAllowanceAmount?: number;
  showDailyAllowance: boolean;
  previewComponents: OtSalaryComponent[];
  baseDays: string;
  baseHours: string | null;
}) => {
  return (
    <DottedGridPanel>
      {!rateType ? (
        <>
          <Box display="flex" alignItems="center" gap="spacing.2">
            <AutomateAccountingIcon size="large" color="interactive.icon.primary.normal" />
            <Text size="medium" weight="semibold">
              Preview
            </Text>
          </Box>
          <Box marginTop="spacing.2">
            <Text size="small" color="surface.text.gray.muted">
              Configure formula to see preview
            </Text>
          </Box>
        </>
      ) : rateType === 'flat' ? (
        <PreviewCard>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap"
            gap="spacing.3">
            <Badge color="information" size="large">
              OT Hours
            </Badge>
            {flatAmount && (
              <>
                <Text as="span" size="large" color="surface.text.gray.muted">
                  ×
                </Text>
                <Badge color="positive" size="large">
                  ₹{flatAmount}
                </Badge>
                <Text as="span" size="large" color="surface.text.gray.muted">
                  per {unit ? FORMULA_UNIT_DISPLAY[unit] : 'hour'}
                </Text>
              </>
            )}
          </Box>
        </PreviewCard>
      ) : (
        <PreviewCard>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap"
            gap="spacing.3">
            <Badge color="information" size="large">
              OT Hours
            </Badge>
            {previewComponents.length > 0 && (
              <>
                <Text as="span" size="large" color="surface.text.gray.muted">
                  × (
                </Text>
                {previewComponents.map((comp, i) => (
                  <React.Fragment key={comp.id}>
                    {i > 0 && (
                      <Text as="span" size="large" color="surface.text.gray.muted">
                        +
                      </Text>
                    )}
                    <Badge color="primary" size="large">
                      {comp.name}
                    </Badge>
                  </React.Fragment>
                ))}
                <Text as="span" size="large" color="surface.text.gray.muted">
                  )
                </Text>
              </>
            )}
            {!!(multiplier && multiplier !== '1') && (
              <>
                <Text as="span" size="large" color="surface.text.gray.muted">
                  ×
                </Text>
                <Badge color="positive" size="large">
                  {multiplier}×
                </Badge>
              </>
            )}
            <Text as="span" size="large" color="surface.text.gray.muted">
              ÷ ({baseDays}d × {baseHours}h)
            </Text>
            {!!(showDailyAllowance && dailyAllowanceAmount) && (
              <>
                <Text as="span" size="large" color="surface.text.gray.muted">
                  +
                </Text>
                <Badge color="notice" size="large">
                  ₹{dailyAllowanceAmount}/day
                </Badge>
              </>
            )}
          </Box>
        </PreviewCard>
      )}
    </DottedGridPanel>
  );
};

const PreviewCard = ({ children }: { children: React.ReactNode }) => (
  <Box
    backgroundColor="surface.background.gray.intense"
    borderWidth="thick"
    borderColor="surface.border.gray.subtle"
    borderRadius="large"
    paddingY="spacing.6"
    paddingX="spacing.6"
    marginTop="spacing.5">
    {children}
  </Box>
);
